import { MenuOptionType } from "@dexteel/mesf-core";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import FeedIcon from "@mui/icons-material/Feed";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import { Box, Button } from "@mui/material";
import { Clip } from "@/shared/models/Clip";
import { ColDef, ColGroupDef, ICellRendererParams } from "ag-grid-community";
import { useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";

type Props = {
	onOpenEdit: (clip: Clip) => void;
	onOpenDelete: (clip: Clip) => void;
	onOpenCreate: () => void;
	showContextMenu: Function;
};

export const useTableData = ({
	onOpenEdit,
	onOpenDelete,
	onOpenCreate,
	showContextMenu,
}: Props) => {
	const navigate = useNavigate();

	// Ref to avoid stale closure in AG Grid cellRenderer
	const showContextMenuRef = useRef(showContextMenu);
	useEffect(() => {
		showContextMenuRef.current = showContextMenu;
	}, [showContextMenu]);

	const baseColumnDefs: (ColDef | ColGroupDef)[] = [
		{
			field: "ClipPartNumber",
			headerName: "Clip Part #",
			flex: 1,
			minWidth: 110,
			wrapHeaderText: true,
		},
		{
			field: "CustomerClipPartNumber",
			headerName: "Customer Clip Part #",
			flex: 1,
			minWidth: 120,
			wrapHeaderText: true,
		},
		{
			field: "Width",
			headerName: "Width",
			flex: 0.5,
			minWidth: 70,
		},
		{
			field: "Height",
			headerName: "Height",
			flex: 0.5,
			minWidth: 70,
		},
		{
			field: "Gauge",
			headerName: "Gauge",
			flex: 0.5,
			minWidth: 70,
		},
		{
			colId: "CoilStockSize",
			headerName: "Coil Stock Size",
			flex: 0.9,
			minWidth: 110,
			wrapHeaderText: true,
			valueGetter: (params) => {
				const clip = params.data as Clip;
				if (clip?.CoilGauge == null && clip?.CoilWidth == null) return "";
				return `${clip.CoilGauge ?? "—"} x ${clip.CoilWidth ?? "—"}`;
			},
		},
		{
			field: "WidthTolerance",
			headerName: "Width Tolerance",
			flex: 0.9,
			minWidth: 120,
			wrapHeaderText: true,
		},
		{
			field: "HeightTolerance",
			headerName: "Height Tolerance",
			flex: 0.9,
			minWidth: 120,
			wrapHeaderText: true,
		},
		{
			field: "MaterialGradeName",
			headerName: "Material Grade",
			flex: 0.8,
			minWidth: 75,
			wrapHeaderText: true,
		},
		{
			field: "TestCheckCycle",
			headerName: "Test Check Cycle",
			flex: 0.7,
			minWidth: 80,
			type: "numericColumn",
			wrapHeaderText: true,
		},
		{
			field: "HolePattern",
			headerName: "Hole Pattern",
			flex: 1,
			minWidth: 120,
			wrapHeaderText: true,
		},
		{
			field: "HoleBreakdown",
			headerName: "Hole Sizes",
			flex: 1.2,
			minWidth: 140,
			wrapHeaderText: true,
		},
		{
			field: "HoleQty",
			headerName: "Total Holes",
			flex: 0.5,
			minWidth: 80,
			type: "numericColumn",
			wrapHeaderText: true,
			valueFormatter: (params) =>
				params.value === 0 || params.value == null ? "" : String(params.value),
		},
	];

	const actionColumnDef: ColDef = {
		field: "-",
		cellRenderer: (params: ICellRendererParams) => {
			const clip = params.data as Clip;
			return (
				<Box
					sx={{
						height: "100%",
						display: "flex",
						justifyContent: "flex-end",
						alignItems: "center",
					}}
				>
					<Box sx={{ display: "flex", gap: 0 }}>
						<Button
							onClick={() => onOpenEdit(clip)}
							title="Edit"
							size="small"
							sx={{ minWidth: 32 }}
						>
							<EditIcon sx={{ fontSize: 18 }} color="primary" />
						</Button>
						<Button
							onClick={() => navigate(`/clips/view/${clip.ClipId}`)}
							title="View Details"
							size="small"
							sx={{ minWidth: 32 }}
						>
							<FeedIcon sx={{ fontSize: 18 }} color="primary" />
						</Button>
						<Button
							onClick={() => onOpenDelete(clip)}
							title="Delete"
							size="small"
							sx={{ minWidth: 32 }}
						>
							<DeleteIcon sx={{ fontSize: 18 }} color="secondary" />
						</Button>
						<Button
							onClick={(e) =>
								showContextMenuRef.current(e.nativeEvent, clip, "clipsContext")
							}
							title="Options"
							size="small"
							sx={{ minWidth: 32 }}
						>
							<FormatListBulletedSharpIcon
								sx={{ fontSize: 18 }}
								color="action"
							/>
						</Button>
					</Box>
				</Box>
			);
		},
		wrapText: false,
		autoHeight: false,
		wrapHeaderText: false,
		headerName: "",
		sortable: false,
		flex: 1,
		maxWidth: 180,
		filter: false,
		autoHeaderHeight: true,
	};

	const columnDefs = useMemo(() => [...baseColumnDefs, actionColumnDef], []);

	const getMenuOptions = (data: Clip) => {
		let options: MenuOptionType[] = [];

		if (data) {
			options.push({
				name: "Edit Clip",
				key: "edit_clip",
				onClick: () => onOpenEdit(data),
				icon: <EditIcon />,
				// First item; matches the row double-click default. Bold to signal it.
				style: { fontWeight: 700 },
			});
			options.push({
				name: "View Details",
				key: "view_details",
				onClick: () => {
					navigate(`/clips/view/${data.ClipId}`);
				},
				icon: <FeedIcon />,
			});
		}

		options.push({
			name: "New Clip",
			key: "new_clip",
			onClick: onOpenCreate,
			icon: <PlaylistAddIcon />,
		});

		if (data) {
			options.push({
				name: "Delete Clip",
				key: "delete_clip",
				onClick: () => onOpenDelete(data),
				icon: <DeleteIcon />,
			});
		}

		return options;
	};

	return { columnDefs, getMenuOptions };
};
